/**
 * RouteErrorBoundary — wraps a route subtree so a render-time exception
 * shows a friendly fallback instead of blanking the whole page (which
 * forced beta testers to log out and back in to recover).
 *
 * Reports to errorReporter so the team sees the stack trace, includes a
 * "Reload" and "Go back" button so the user can self-recover, and resets
 * its state when the URL changes so navigating to another route clears
 * the error.
 */
import { Component, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { reportBoundaryError } from '@/utils/errorReporter';

interface Props {
  children: ReactNode;
  /** Optional label shown in the fallback header — e.g. "Site Detail". */
  routeName?: string;
}

interface State {
  error: Error | null;
}

class _RouteErrorBoundaryClass extends Component<
  Props & { onResetKey: string; onGoBack: () => void; onReload: () => void },
  State
> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, _info: { componentStack?: string }) {
    reportBoundaryError(error);
  }

  componentDidUpdate(prevProps: Readonly<Props & { onResetKey: string }>) {
    // Reset when the URL changes — so navigating away from the broken
    // route clears the error and the next page renders normally.
    if (prevProps.onResetKey !== this.props.onResetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
          <div className="text-mono-300 text-xs tracking-widest mb-3">
            SOMETHING WENT WRONG
          </div>
          <div className="text-mono-500 text-sm max-w-md mb-6">
            {this.props.routeName ? `The ${this.props.routeName} page` : 'This page'} hit an unexpected
            error. The team has been notified — try reloading or heading back.
          </div>
          <div className="flex gap-3">
            <button
              onClick={this.props.onReload}
              className="px-4 py-2 text-xs tracking-widest bg-cyan-600/20 border border-cyan-600/40 text-cyan-300 rounded hover:bg-cyan-600/30 transition-colors"
            >
              RELOAD
            </button>
            <button
              onClick={this.props.onGoBack}
              className="px-4 py-2 text-xs tracking-widest bg-mono-800 border border-mono-700 text-mono-300 rounded hover:bg-mono-700 transition-colors"
            >
              GO BACK
            </button>
          </div>
          {import.meta.env.DEV && (
            <details className="mt-8 max-w-2xl text-left">
              <summary className="text-mono-600 text-[10px] tracking-widest cursor-pointer">
                ERROR DETAILS (DEV ONLY)
              </summary>
              <pre className="mt-2 text-[10px] text-red-400 bg-mono-900 p-3 rounded overflow-auto">
                {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export function RouteErrorBoundary({ children, routeName }: Props) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <_RouteErrorBoundaryClass
      // Reset the boundary when the URL changes so the user can navigate
      // out of a broken route without a hard reload.
      onResetKey={location.pathname + location.search}
      onGoBack={() => navigate(-1)}
      onReload={() => window.location.reload()}
      routeName={routeName}
    >
      {children}
    </_RouteErrorBoundaryClass>
  );
}
